import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePergunta } from "../contexts/PerguntaContext";
import { useSalvarConversaNoStorage } from "../hooks/SalvarConversaNoStorage";
import { AvaliarMensagemGpt } from "../hooks/AvaliarMensagemGpt";
import { useFalarComAtendente } from "../hooks/FalarComAtendente";
import CartaoSemPlano from "./CartaoSemPlano";
import IconeWhatsApp from "./IconeWhatsApp";
import { registrarEvento, MotivoNaoResolveu, OrigemAtendente } from "../lib/metricas";

interface BarraResolveuProps {
  mensagemId: string;
  slugAtual: string;
  idRespostaGptSak?: number | null;
  feedback?: "sim" | "nao";
  perguntaOriginal: string;
  // A IA disse que não consegue resolver: oferece o atendente já na primeira linha.
  sugerirHumano?: boolean;
  // Métricas: número da resposta e por que o atendente aparece na primeira linha.
  registro?: number | null;
  origemAtendente?: OrigemAtendente;
}

type Etapa = "pergunta" | "resolvido" | "opcoes" | "detalhar" | "sem_plano";

export const focarCampoDuvida = () =>
  document.getElementById("input-duvida-conversa")?.focus();

// Substitui o 👍/👎 e o botão verde "Mais ajuda" que aparecia em toda resposta: primeiro pergunta
// se resolveu; quando não, tenta de novo com a IA e só então oferece o atendente. Depois de
// duas respostas que não resolveram, o atendente aparece já na primeira linha (sugerirHumano).
const BarraResolveu: React.FC<BarraResolveuProps> = ({
  mensagemId,
  slugAtual,
  idRespostaGptSak,
  feedback,
  perguntaOriginal,
  sugerirHumano = false,
  registro = null,
  origemAtendente = "sugerido",
}) => {
  const { t } = useTranslation();
  const { setPergunta, conversaAtual, setContextoPendente } = usePergunta();
  const { salvarConversa, atualizarMensagem } = useSalvarConversaNoStorage();
  const { avaliarResposta } = AvaliarMensagemGpt();
  const { acesso, chamarAtendente } = useFalarComAtendente(slugAtual);

  const [etapa, setEtapa] = useState<Etapa>(
    feedback === "sim" ? "resolvido" : feedback === "nao" ? "opcoes" : "pergunta"
  );

  // Quando o cartão "O que faltou?" abre no fim da conversa, rola só o necessário para mostrá-lo.
  const raiz = useRef<HTMLDivElement>(null);
  const primeiraRenderizacao = useRef(true);
  useEffect(() => {
    if (primeiraRenderizacao.current) {
      primeiraRenderizacao.current = false;
      return;
    }
    raiz.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [etapa]);

  const evento = (tipo: "resolveu" | "nao_resolveu" | "motivo" | "atendente", detalhe?: MotivoNaoResolveu | OrigemAtendente) =>
    registrarEvento({ tipo, conversa: conversaAtual, app: slugAtual, registro, detalhe });

  const responder = (resolveu: boolean) => {
    if (idRespostaGptSak) avaliarResposta(idRespostaGptSak, resolveu ? 2 : 1);
    evento(resolveu ? "resolveu" : "nao_resolveu");
    atualizarMensagem(slugAtual, conversaAtual, mensagemId, {
      feedback: resolveu ? "sim" : "nao",
    });
    setEtapa(resolveu ? "resolvido" : "opcoes");
  };

  const pedirOutraExplicacao = () => {
    salvarConversa(slugAtual, conversaAtual, { usuario: t("fb_msg_nao_entendi") });
    setPergunta(`${perguntaOriginal}\n\n${t("fb_api_simplificar")}`);
  };

  const darMaisDetalhes = () => {
    setContextoPendente(perguntaOriginal);
    setEtapa("detalhar");
    focarCampoDuvida();
  };

  const outraDuvida = () => {
    setContextoPendente(null);
    focarCampoDuvida();
  };

  const falarComAtendente = (origem: OrigemAtendente) => {
    evento("atendente", origem);
    if (acesso === "sem_plano") {
      setEtapa("sem_plano");
      return;
    }
    chamarAtendente();
  };

  const conteudo = () => {
    if (etapa === "resolvido") {
      return <p className="barra-resolveu-confirmacao">{t("fb_que_bom")}</p>;
    }

    if (etapa === "sem_plano") {
      return <CartaoSemPlano aoContinuarComIa={outraDuvida} />;
    }

    if (etapa === "pergunta") {
      return (
        <div className="barra-resolveu">
          <span className="barra-resolveu-titulo">{t("fb_isso_resolveu")}</span>
          <button className="botao-primario" onClick={() => responder(true)}>
            {t("fb_sim_resolveu")}
          </button>
          <button className="botao-secundario" onClick={() => responder(false)}>
            {t("fb_nao_ainda")}
          </button>
          {sugerirHumano && acesso !== "verificando" && (
            <button className="botao-secundario opcao-atendente" onClick={() => falarComAtendente(origemAtendente)}>
              <IconeWhatsApp />
              {t("fb_falar_atendente")}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="cartao-suporte">
        <p className="cartao-suporte-titulo">{t("fb_o_que_faltou")}</p>
        {etapa === "detalhar" ? (
          <>
            <p className="cartao-suporte-texto">{t("fb_dica_detalhes")}</p>
            {/* Quem vai detalhar ainda pode desistir da IA: sem isso a etapa era um beco sem saída. */}
            {acesso !== "verificando" && (
              <div className="opcoes-resposta">
                <button className="opcao opcao-atendente" onClick={() => falarComAtendente("detalhar")}>
                  <IconeWhatsApp />
                  {t("fb_falar_atendente")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="opcoes-resposta">
            <button className="opcao" onClick={() => { evento("motivo", "nao_entendi"); pedirOutraExplicacao(); }}>
              {t("fb_nao_entendi")}
            </button>
            <button className="opcao" onClick={() => { evento("motivo", "mais_detalhes"); darMaisDetalhes(); }}>
              {t("fb_nao_resolveu")}
            </button>
            <button className="opcao" onClick={() => { evento("motivo", "duvida_outra"); outraDuvida(); }}>
              {t("fb_duvida_outra")}
            </button>
            {acesso !== "verificando" && (
              <button className="opcao opcao-atendente" onClick={() => falarComAtendente("cartao")}>
                <IconeWhatsApp />
                {t("fb_falar_atendente")}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return <div ref={raiz}>{conteudo()}</div>;
};

export default BarraResolveu;
